import React, { useState, useEffect } from "react";
import { 
  Mail, 
  Send, 
  ArrowLeft,
  CheckCircle,
  User,
  MessageCircle,
  Sparkles,
  Star
} from "lucide-react";
import { motion } from "framer-motion";

export default function ContactUs() {
  const [darkMode, setDarkMode] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const checkDarkMode = () => {
      const isDark = document.documentElement.classList.contains('dark');
      setDarkMode(isDark);
    };
    
    checkDarkMode();
    
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    return () => observer.disconnect();
  }, []);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
    
    if (name === 'email' && value.trim()) {
      if (!validateEmail(value.trim())) {
        setErrors(prev => ({ 
          ...prev, 
          [name]: "Please enter a valid email address" 
        }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }
    
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(formData.email.trim())) {
      newErrors.email = "Please enter a valid email address";
    }
    
    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(
        "https://api.zenithacademics.com/api/contact",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        let errorMessage = `Server responded with ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {}
        throw new Error(errorMessage);
      }

      const data = await response.json();
      setSubmitted(true);
    } catch (error) {
      console.error("Error submitting contact form:", error);
      alert(`Failed to submit message: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReturnHome = () => {
    window.location.href = '/';
  };

  const handleSendAnother = () => {
    setSubmitted(false);
    setFormData({
      name: "",
      email: "",
      message: ""
    });
    setErrors({});
  };

  const generateStars = () => {
    const stars = [];
    for (let i = 0; i < 40; i++) {
      const size = Math.random() * 3 + 1;
      const x = Math.random() * 100;
      const y = Math.random() * 100;
      const duration = Math.random() * 5 + 3;
      const delay = Math.random() * 5;
      
      stars.push(
        <motion.div
          key={i}
          className={`absolute rounded-full ${darkMode ? 'bg-white/80' : 'bg-blue-400/70'}`}
          style={{
            width: `${size}px`,
            height: `${size}px`,
            left: `${x}%`,
            top: `${y}%`,
          }}
          animate={{
            opacity: [0.3, 1, 0.3],
            scale: [1, 1.5, 1],
          }}
          transition={{
            duration: duration,
            repeat: Infinity,
            delay: delay,
            ease: "easeInOut"
          }}
        />
      );
    }
    return stars;
  };

  if (submitted) {
    return (
      <div className={`min-h-screen flex flex-col items-center justify-center px-4 ${darkMode ? 'bg-gradient-to-b from-zinc-950 via-zinc-900 to-zinc-950' : 'bg-gradient-to-br from-white via-blue-50 to-purple-50'} relative overflow-hidden`}>
        {/* Star Background */}
        <div className="absolute inset-0">
          {generateStars()}
        </div>
        
        {/* Glowing Orbs */}
        <motion.div 
          className={`absolute top-1/4 left-1/4 w-64 h-64 rounded-full blur-3xl opacity-30 ${darkMode ? 'bg-red-600/40' : 'bg-blue-400/40'}`}
          animate={{
            scale: [1, 1.2, 1],
            x: [0, 20, 0],
            y: [0, -20, 0]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        
        <motion.div 
          className={`absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full blur-3xl opacity-20 ${darkMode ? 'bg-purple-600/30' : 'bg-purple-400/30'}`}
          animate={{
            scale: [1, 1.3, 1],
            x: [0, -30, 0],
            y: [0, 30, 0]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        />

        {/* Navigation Button */}
        <div className="absolute top-6 left-6 z-50">
          <motion.button
            onClick={handleReturnHome}
            className={`flex items-center gap-3 px-6 py-3 rounded-xl font-semibold shadow-lg backdrop-blur-xl border-2 ${darkMode ? 'bg-zinc-900/60 hover:bg-zinc-800/80 text-white border-zinc-700/50' : 'bg-white/60 hover:bg-white/80 text-gray-700 border-gray-200/50'}`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <ArrowLeft className="w-5 h-5" />
            Return to Home
          </motion.button>
        </div>

        <motion.div 
          className="w-full max-w-2xl z-10"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
        >
          <div className={`p-8 sm:p-12 text-center rounded-3xl shadow-2xl border-4 transition-colors backdrop-blur-xl
            ${darkMode ? 'bg-zinc-900/60 border-red-500/40' : 'bg-white/80 border-blue-200'}
          `}>
            <motion.div 
              className={`w-24 h-24 sm:w-32 sm:h-32 ${darkMode ? 'bg-gradient-to-br from-red-500 to-orange-600 shadow-[0_0_40px_rgba(239,68,68,0.5)]' : 'bg-gradient-to-br from-blue-500 to-purple-600 shadow-[0_0_40px_rgba(59,130,246,0.4)]'} rounded-full flex items-center justify-center mx-auto mb-6 sm:mb-8`}
              initial={{ scale: 0 }}
              animate={{ 
                scale: 1, 
                rotate: 360,
                boxShadow: darkMode 
                  ? ['0 0 40px rgba(239,68,68,0.5)', '0 0 60px rgba(249,115,22,0.6)', '0 0 40px rgba(239,68,68,0.5)']
                  : ['0 0 40px rgba(59,130,246,0.4)', '0 0 60px rgba(147,51,234,0.5)', '0 0 40px rgba(59,130,246,0.4)']
              }}
              transition={{ 
                delay: 0.2, 
                type: "spring",
                boxShadow: { duration: 2, repeat: Infinity }
              }}
            >
              <CheckCircle className="w-12 h-12 sm:w-16 sm:h-16 text-white" />
            </motion.div>
            
            <motion.h2 
              className={`text-3xl sm:text-4xl md:text-5xl font-black mb-4 sm:mb-6 bg-gradient-to-r ${darkMode ? 'from-red-400 via-orange-400 to-amber-400' : 'from-blue-600 via-purple-600 to-pink-600'} bg-clip-text text-transparent pb-2`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              Message Sent!
            </motion.h2>
            
            <motion.p 
              className={`text-lg sm:text-xl ${darkMode ? 'text-zinc-300' : 'text-gray-600'} mb-8 sm:mb-10 leading-relaxed`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              Thank you for reaching out! We'll get back to you within 24 hours.
            </motion.p>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <motion.button
                onClick={handleSendAnother}
                className={`px-6 py-4 sm:px-8 sm:py-4 rounded-xl font-bold transition-all shadow-lg border-2 backdrop-blur-sm ${darkMode ? 'bg-zinc-800/60 hover:bg-zinc-700/80 border-zinc-700 text-white' : 'bg-white/60 hover:bg-gray-200 border-gray-200 text-gray-700'} hover:scale-105`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Send Another Message
              </motion.button>
              <motion.button
                onClick={handleReturnHome}
                className={`px-6 py-4 sm:px-8 sm:py-4 rounded-xl font-bold transition-all shadow-lg backdrop-blur-sm ${darkMode ? 'bg-gradient-to-r from-red-500 to-orange-600 text-white' : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'} hover:scale-105 hover:shadow-xl`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Return to Home
              </motion.button>
            </motion.div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen py-20 ${darkMode ? 'bg-gradient-to-b from-zinc-950 via-zinc-900 to-zinc-950' : 'bg-gradient-to-br from-white via-blue-50 to-purple-50'} relative overflow-hidden transition-colors duration-300`}>
      {/* Starry Background */}
      <div className="absolute inset-0">
        {generateStars()}
      </div>
      
      {/* Animated Glowing Orbs */}
      <motion.div 
        className={`absolute top-20 left-10 w-96 h-96 rounded-full blur-3xl ${darkMode ? 'bg-red-600/20' : 'bg-blue-400/20'}`}
        animate={{
          scale: [1, 1.2, 1],
          x: [0, 50, 0],
          y: [0, -30, 0]
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      
      <motion.div 
        className={`absolute bottom-20 right-10 w-80 h-80 rounded-full blur-3xl ${darkMode ? 'bg-purple-600/20' : 'bg-purple-400/20'}`}
        animate={{
          scale: [1, 1.3, 1],
          x: [0, -40, 0],
          y: [0, 40, 0]
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2
        }}
      />
      
      <motion.div 
        className={`absolute top-1/3 right-1/3 w-64 h-64 rounded-full blur-3xl ${darkMode ? 'bg-amber-500/15' : 'bg-pink-400/20'}`}
        animate={{
          scale: [1, 1.1, 1],
          rotate: [0, 180, 360]
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "linear"
        }}
      />

      {/* Navigation Buttons */}
      <div className="fixed top-6 left-6 z-50">
        <motion.button
          onClick={() => window.location.href = '/'}
          className={`flex items-center gap-3 px-6 py-3 rounded-xl font-semibold shadow-lg backdrop-blur-xl border-2 ${darkMode ? 'bg-zinc-900/60 hover:bg-zinc-800/80 text-white border-zinc-700/50' : 'bg-white/60 hover:bg-white/80 text-gray-700 border-gray-200/50'}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <ArrowLeft className="w-5 h-5" />
          Return to Home
        </motion.button>
      </div>

      <div className="fixed top-6 right-6 z-50">
        <motion.button
          onClick={() => setDarkMode(!darkMode)}
          className={`p-4 rounded-2xl backdrop-blur-xl shadow-xl border-2 ${darkMode ? 'bg-zinc-900/60 text-yellow-400 border-zinc-700/50' : 'bg-white/60 text-gray-700 border-gray-200/50'}`}
          whileHover={{ scale: 1.1, rotate: 180 }}
          whileTap={{ scale: 0.9 }}
        >
          <span className="text-2xl">{darkMode ? '☀️' : '🌙'}</span>
        </motion.button>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header Section */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className="inline-flex items-center gap-2 mb-8"
            animate={{ rotate: [0, 10, 0, -10, 0] }}
            transition={{ duration: 5, repeat: Infinity }}
          >
            <Star className={`w-8 h-8 ${darkMode ? 'text-amber-400' : 'text-blue-500'}`} />
            <Sparkles className={`w-8 h-8 ${darkMode ? 'text-purple-400' : 'text-purple-500'}`} />
            <Star className={`w-8 h-8 ${darkMode ? 'text-red-400' : 'text-pink-500'}`} />
          </motion.div>
          
          <h1 className={`text-5xl md:text-7xl font-black mb-6 leading-tight bg-gradient-to-r ${darkMode ? 'from-red-400 via-orange-400 to-amber-400' : 'from-blue-600 via-purple-600 to-pink-600'} bg-clip-text text-transparent`}>
            Get In Touch
          </h1>
          
          <motion.p 
            className={`text-xl md:text-2xl ${darkMode ? 'text-zinc-300' : 'text-gray-600'} mb-4 max-w-2xl mx-auto`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            Reach out and let's create something amazing together
          </motion.p>
          <motion.p 
            className={`${darkMode ? 'text-zinc-400' : 'text-gray-500'} text-lg`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            We'll respond within 24 hours
          </motion.p>
        </motion.div>

        {/* Centered Form Container */}
        <div className="flex justify-center">
          <motion.div 
            className="w-full max-w-3xl relative"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {/* Stars around the form - tastefully placed */}
            {/* Top left star */}
            <motion.div
              className="absolute -top-4 -left-4"
              animate={{
                y: [0, -8, 0],
                rotate: [0, 90, 180, 270, 360]
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <Star className={`w-6 h-6 ${darkMode ? 'text-amber-300/60' : 'text-blue-400/60'}`} />
            </motion.div>
            
            {/* Top right star */}
            <motion.div
              className="absolute -top-4 -right-4"
              animate={{
                y: [0, -6, 0],
                rotate: [0, -90, -180, -270, -360]
              }}
              transition={{
                duration: 7,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 1
              }}
            >
              <Star className={`w-5 h-5 ${darkMode ? 'text-purple-300/60' : 'text-purple-400/60'}`} />
            </motion.div>
            
            {/* Bottom left star */}
            <motion.div
              className="absolute -bottom-4 -left-6"
              animate={{
                y: [0, 8, 0],
                scale: [1, 1.2, 1]
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 0.5
              }}
            >
              <Star className={`w-7 h-7 ${darkMode ? 'text-red-300/50' : 'text-pink-400/50'}`} />
            </motion.div>
            
            {/* Bottom right star */}
            <motion.div
              className="absolute -bottom-6 -right-4"
              animate={{
                y: [0, 6, 0],
                scale: [1, 1.3, 1]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 1.5
              }}
            >
              <Sparkles className={`w-6 h-6 ${darkMode ? 'text-blue-300/60' : 'text-cyan-400/60'}`} />
            </motion.div>
            
            {/* Left middle star */}
            <motion.div
              className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2"
              animate={{
                x: [0, -10, 0],
                opacity: [0.4, 1, 0.4]
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 0.8
              }}
            >
              <Star className={`w-4 h-4 ${darkMode ? 'text-green-300/40' : 'text-emerald-400/40'}`} />
            </motion.div>
            
            {/* Right middle star */}
            <motion.div
              className="absolute right-0 top-1/2 translate-x-1/2 -translate-y-1/2"
              animate={{
                x: [0, 10, 0],
                opacity: [0.4, 1, 0.4]
              }}
              transition={{
                duration: 9,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 0.3
              }}
            >
              <Star className={`w-4 h-4 ${darkMode ? 'text-yellow-300/40' : 'text-yellow-400/40'}`} />
            </motion.div>
            
            {/* Additional subtle stars in the corners */}
            <motion.div
              className="absolute top-8 -left-2"
              animate={{
                rotate: [0, 360],
                scale: [0.8, 1, 0.8]
              }}
              transition={{
                duration: 12,
                repeat: Infinity,
                ease: "linear"
              }}
            >
              <div className={`w-2 h-2 rounded-full ${darkMode ? 'bg-blue-400/30' : 'bg-blue-500/30'}`} />
            </motion.div>
            
            <motion.div
              className="absolute top-8 -right-2"
              animate={{
                rotate: [0, -360],
                scale: [0.8, 1, 0.8]
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                ease: "linear"
              }}
            >
              <div className={`w-2 h-2 rounded-full ${darkMode ? 'bg-purple-400/30' : 'bg-purple-500/30'}`} />
            </motion.div>
            
            <motion.div
              className="absolute bottom-8 -left-2"
              animate={{
                rotate: [0, 360],
                scale: [1, 0.8, 1]
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: "linear"
              }}
            >
              <div className={`w-1.5 h-1.5 rounded-full ${darkMode ? 'bg-red-400/30' : 'bg-pink-500/30'}`} />
            </motion.div>
            
            <motion.div
              className="absolute bottom-8 -right-2"
              animate={{
                rotate: [0, -360],
                scale: [1, 0.8, 1]
              }}
              transition={{
                duration: 9,
                repeat: Infinity,
                ease: "linear"
              }}
            >
              <div className={`w-1.5 h-1.5 rounded-full ${darkMode ? 'bg-amber-400/30' : 'bg-amber-500/30'}`} />
            </motion.div>

            <div className={`p-12 rounded-3xl shadow-2xl border-4 transition-all backdrop-blur-xl ${darkMode ? 'bg-zinc-900/60 border-red-500/40 hover:border-red-500/60' : 'bg-white/80 border-blue-200/60 hover:border-blue-400/80'}`}>
              <motion.div 
                className="text-center mb-10"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <motion.div className={`w-20 h-20 rounded-2xl flex items-center justify-center shadow-xl mx-auto mb-6 bg-gradient-to-br ${darkMode ? 'from-red-500 to-orange-600 shadow-[0_0_30px_rgba(239,68,68,0.4)]' : 'from-blue-600 to-purple-600 shadow-[0_0_30px_rgba(59,130,246,0.3)]'}`}>
                  <Send className="w-10 h-10 text-white" />
                </motion.div>
                
                <h2 className={`text-4xl font-black mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Send Your Message
                </h2>
                <p className={`${darkMode ? 'text-red-300' : 'text-blue-600'} font-semibold text-lg mb-2`}>
                  We're excited to hear from you!
                </p>
                <div className="flex justify-center gap-2 mt-4">
                  {[...Array(3)].map((_, i) => (
                    <motion.div
                      key={i}
                      className={`w-2 h-2 rounded-full ${darkMode ? 'bg-red-500/60' : 'bg-blue-500/60'}`}
                      animate={{ scale: [1, 1.5, 1] }}
                      transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                    />
                  ))}
                </div>
              </motion.div>

              <form onSubmit={handleSubmit} className="space-y-10">
                <div className="grid md:grid-cols-2 gap-10">
                  {/* Name Field */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    <label className={`block text-sm font-bold mb-4 ${darkMode ? 'text-zinc-300' : 'text-gray-700'}`}>
                      Your Name *
                    </label>
                    <div className="relative">
                      <div className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 rounded-xl flex items-center justify-center ${darkMode ? 'bg-zinc-800/80' : 'bg-blue-50'}`}>
                        <User className={`w-6 h-6 ${darkMode ? 'text-zinc-400' : 'text-blue-500'}`} />
                      </div>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={`w-full pl-16 pr-6 py-5 text-lg border-3 ${errors.name ? 'border-red-500' : darkMode ? 'border-red-500/30 focus:border-red-400' : 'border-blue-300 focus:border-blue-500'} ${darkMode ? 'bg-zinc-800/40 text-white placeholder-zinc-400' : 'bg-white text-gray-900 placeholder-gray-400'} rounded-xl focus:ring-4 ${errors.name ? 'focus:ring-red-500/30' : darkMode ? 'focus:ring-red-500/20' : 'focus:ring-blue-300'} transition-all shadow-lg hover:shadow-xl`}
                        placeholder="Enter your name"
                      />
                    </div>
                    {errors.name && <motion.p 
                      className="text-red-500 text-sm mt-3 font-medium"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >{errors.name}</motion.p>}
                  </motion.div>

                  {/* Email Field */}
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.7 }}
                  >
                    <label className={`block text-sm font-bold mb-4 ${darkMode ? 'text-zinc-300' : 'text-gray-700'}`}>
                      Your Email *
                    </label>
                    <div className="relative">
                      <div className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 rounded-xl flex items-center justify-center ${darkMode ? 'bg-zinc-800/80' : 'bg-purple-50'}`}>
                        <Mail className={`w-6 h-6 ${darkMode ? 'text-zinc-400' : 'text-purple-500'}`} />
                      </div>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full pl-16 pr-6 py-5 text-lg border-3 ${errors.email ? 'border-red-500' : darkMode ? 'border-red-500/30 focus:border-red-400' : 'border-blue-300 focus:border-blue-500'} ${darkMode ? 'bg-zinc-800/40 text-white placeholder-zinc-400' : 'bg-white text-gray-900 placeholder-gray-400'} rounded-xl focus:ring-4 ${errors.email ? 'focus:ring-red-500/30' : darkMode ? 'focus:ring-red-500/20' : 'focus:ring-blue-300'} transition-all shadow-lg hover:shadow-xl`}
                        placeholder="your@email.com"
                        inputMode="email"
                      />
                    </div>
                    {errors.email && <p className="text-red-500 text-sm mt-3 font-medium">{errors.email}</p>}
                  </motion.div>
                </div>

                {/* Message Field */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                >
                  <label className={`block text-sm font-bold mb-4 ${darkMode ? 'text-zinc-300' : 'text-gray-700'}`}>
                    Your Message *
                  </label>
                  <div className="relative">
                    <div className={`absolute left-4 top-4 w-12 h-12 rounded-xl flex items-center justify-center ${darkMode ? 'bg-zinc-800/80' : 'bg-pink-50'}`}>
                      <MessageCircle className={`w-6 h-6 ${darkMode ? 'text-zinc-400' : 'text-pink-500'}`} />
                    </div>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows="8"
                      className={`w-full pl-16 pr-6 py-5 text-lg border-3 ${errors.message ? 'border-red-500' : darkMode ? 'border-red-500/30 focus:border-red-400' : 'border-blue-300 focus:border-blue-500'} ${darkMode ? 'bg-zinc-800/40 text-white placeholder-zinc-400' : 'bg-white text-gray-900 placeholder-gray-400'} rounded-xl focus:ring-4 ${errors.message ? 'focus:ring-red-500/30' : darkMode ? 'focus:ring-red-500/20' : 'focus:ring-blue-300'} transition-all resize-none shadow-lg hover:shadow-xl`}
                      placeholder="Share your thoughts with us..."
                    />
                  </div>
                  {errors.message && <motion.p 
                    className="text-red-500 text-sm mt-3 font-medium"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >{errors.message}</motion.p>}
                </motion.div>

                {/* Submit Button */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9 }}
                >
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full flex items-center justify-center gap-4 px-10 py-6 rounded-xl font-bold shadow-2xl transition-all backdrop-blur-sm ${darkMode ? 'bg-gradient-to-r from-red-500 to-orange-600 hover:shadow-[0_0_60px_rgba(239,68,68,0.6)]' : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:shadow-[0_0_60px_rgba(147,51,234,0.5)]'} text-white disabled:opacity-70 disabled:cursor-not-allowed`}
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    animate={isSubmitting ? {
                      background: darkMode 
                        ? ['linear-gradient(to right, #ef4444, #f97316)', 'linear-gradient(to right, #dc2626, #ea580c)']
                        : ['linear-gradient(to right, #2563eb, #7c3aed)', 'linear-gradient(to right, #1d4ed8, #6d28d9)']
                    } : {}}
                    transition={isSubmitting ? {
                      background: { duration: 1, repeat: Infinity, ease: "easeInOut" }
                    } : {}}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-7 h-7 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-xl">Sending...</span>
                      </>
                    ) : (
                      <>
                        <span className="text-xl">Send Message</span>
                        <Send className="w-7 h-7" />
                      </>
                    )}
                  </motion.button>
                </motion.div>
              </form>
            </div>
          </motion.div>
        </div>

        {/* Decorative Footer */}
        <motion.div 
          className="mt-16 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          <div className="flex justify-center gap-4 mb-4">
            {[...Array(5)].map((_, i) => (
              <motion.div
                key={i}
                className={`w-1 h-1 rounded-full ${darkMode ? 'bg-amber-400/40' : 'bg-blue-400/40'}`}
                animate={{ scale: [1, 1.5, 1] }}
                transition={{ duration: 2, repeat: Infinity, delay: i * 0.2 }}
              />
            ))}
          </div>
          <p className={`${darkMode ? 'text-zinc-400' : 'text-gray-500'} text-lg`}>
            Your message is important to us
          </p>
          <p className={`mt-2 ${darkMode ? 'text-zinc-500' : 'text-gray-400'} text-sm`}>
            We'll respond as soon as possible
          </p>
        </motion.div>
      </div>
    </div>
  );
}